import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { DSVOfficeEntity } from '../types/dsvoffice.type';
import { ENV } from '@app/constants/env';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getToken } from '../../../src/helpers/authProvider';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${ENV.BASE_API_URL +ENV.MASTERS_APIURL}`,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      // Check if body is FormData - if so, don't create new Headers object
      // because it will lose the multipart boundary
      if ((args as FetchArgs).body instanceof FormData) {
        // For FormData, just add Authorization to existing headers or create minimal headers
        const headers = new Headers();
        headers.set('Authorization', `Bearer ${token}`);
        (args as FetchArgs).headers = headers;
        // Don't set Content-Type - browser will set it with boundary
      } else {
        const headers =
          (args as FetchArgs).headers instanceof Headers
            ? (args as FetchArgs).headers
            : new Headers((args as FetchArgs).headers);

        headers.set('Authorization', `Bearer ${token}`);
        (args as FetchArgs).headers = headers;
      }
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // Handle token refresh if needed
    }

    return result;
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

export const dsvofficeApi = createApi({
  reducerPath: 'dsvofficeApi',
  baseQuery: authBaseQuery,
  tagTypes: ['DSVOffice'],
  endpoints: (builder) => ({    
    getAllDSVOffices: builder.query<DSVOfficeEntity[], void>({
      query: () => ({
        url: 'DsvOffice/GetAllDsvOffices',
        method: 'GET',
      }),
      providesTags: ['DSVOffice']
    }),
    
    getDSVOfficeById: builder.query<DSVOfficeEntity, number>({
      query: (id) => ({
        url: `DsvOffice/GetDsvOfficeById/${id}`,
        method: 'GET',
      }),
      providesTags: ['DSVOffice']
    }),    createDSVOffice: builder.mutation<any, any>({
      query: (dsvoffice) => ({
        url: 'DsvOffice/CreateDsvOffice',
        method: 'POST',
        body: dsvoffice,
      }),
      invalidatesTags: ['DSVOffice']
    }),updateDSVOffice: builder.mutation<DSVOfficeEntity, Partial<DSVOfficeEntity>>({
      query: (dsvoffice) => ({
        url: `DsvOffice/UpdateDsvOffice/${dsvoffice.mstr_consignee_id}`,
        method: 'PUT',
        body: dsvoffice,
      }),
      invalidatesTags: ['DSVOffice']
    })
  }),
});

export const {
  useGetAllDSVOfficesQuery,
  useGetDSVOfficeByIdQuery,
  useCreateDSVOfficeMutation,
  useUpdateDSVOfficeMutation,
} = dsvofficeApi;
